import { useState, useEffect, useCallback, useRef } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// All available columns from the Reservation table
const ALL_COLUMNS = [
  { key: "reservation_id", label: "Reservation ID", defaultVisible: true },
  { key: "guest_name", label: "Guest Name", defaultVisible: true },
  { key: "actual_checkin", label: "Actual Check-in", defaultVisible: true },
  { key: "actual_checkout", label: "Actual Check-out", defaultVisible: true },
  { key: "account_holder_name", label: "Account Holder Name", defaultVisible: true },
  { key: "account_number", label: "Account Number", defaultVisible: true },
  { key: "bank_name", label: "Bank Name", defaultVisible: true },
  { key: "listing_name", label: "Listing Name", defaultVisible: false },
  { key: "status", label: "Status", defaultVisible: false },
  { key: "phone", label: "Phone", defaultVisible: false },
  { key: "number_of_guests", label: "No. of Guests", defaultVisible: false },
  { key: "channel_name", label: "Channel", defaultVisible: false },
  { key: "booking_date", label: "Booking Date", defaultVisible: false },
  { key: "currency", label: "Currency", defaultVisible: false },
  { key: "total_price", label: "Total Price", defaultVisible: false },
  { key: "total_paid", label: "Total Paid", defaultVisible: false },
  { key: "payment_status", label: "Payment Status", defaultVisible: false },
  { key: "cleanness_status", label: "Cleanliness Status", defaultVisible: false },
  { key: "actions", label: "Actions", defaultVisible: true },
];

const DEFAULT_VISIBLE = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

function getTodayPKT() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
}

function formatDateDisplay(isoDate) {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (isNaN(d)) return isoDate;
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Karachi", day: "2-digit", month: "short", year: "numeric" });
}

function isWithinRange(isoDate, start, end) {
  if (!isoDate) return false;
  const recordDay = new Date(isoDate).toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });
  if (!start && !end) return true;
  if (start && end) return recordDay >= start && recordDay <= end;
  if (start) return recordDay >= start;
  if (end) return recordDay <= end;
  return false;
}

function SecurityDepositRefund() {
  const CACHE_KEY = "sd_refund_cached_records";
  const [allRows, setAllRows] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return [];
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem(CACHE_KEY));
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [startDate, setStartDate] = useState(getTodayPKT());
  const [endDate, setEndDate] = useState(getTodayPKT());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [showPending, setShowPending] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef(null);

  // Close column menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) {
        setColMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    if (newStart && endDate) {
      const diff = (new Date(endDate) - new Date(newStart)) / (1000 * 60 * 60 * 24);
      if (diff > 30) {
        setSnackbar({ open: true, message: "Date range cannot exceed 31 days.", severity: "warning" });
        return;
      }
      if (diff < 0) {
        setSnackbar({ open: true, message: "Start Date cannot be after End Date.", severity: "warning" });
        return;
      }
    }
    setStartDate(newStart);
  };

  const handleEndDateChange = (e) => {
    const newEnd = e.target.value;
    if (startDate && newEnd) {
      const diff = (new Date(newEnd) - new Date(startDate)) / (1000 * 60 * 60 * 24);
      if (diff > 30) {
        setSnackbar({ open: true, message: "Date range cannot exceed 31 days.", severity: "warning" });
        return;
      }
      if (diff < 0) {
        setSnackbar({ open: true, message: "End Date cannot be before Start Date.", severity: "warning" });
        return;
      }
    }
    setEndDate(newEnd);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const fetchAllRecords = useCallback(async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await axios.get("/api/security-deposit-refunds", {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        params: {
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });
      const allRecords = res.data.records || [];
      if (allRecords.length > 0) {
        localStorage.setItem(CACHE_KEY, JSON.stringify(allRecords));
      }
      setAllRows(allRecords);
    } catch (err) {
      console.error("SD Refund fetch error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load data.");
    } finally {
      if (showLoadingIndicator) setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const hasCache = Boolean(localStorage.getItem(CACHE_KEY));
    fetchAllRecords(!hasCache);
    const interval = setInterval(() => fetchAllRecords(false), 120000);
    return () => clearInterval(interval);
  }, [fetchAllRecords]);

  // Filter by actual_checkout date
  const dateFilteredRows = allRows.filter((row) => isWithinRange(row["actual_checkout"], startDate, endDate));

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleSendReminder = async (reservationId) => {
    if (!reservationId) return;
    try {
      setSnackbar({ open: true, message: `Sending reminder...`, severity: "info" });
      await axios.post("https://n8n.namuve.com/webhook/a084e3a7-39ba-4d9f-8765-4a8707044d07", {
        hostawayreservation_id: reservationId,
        event: "Checked-Out"
      });
      setSnackbar({ open: true, message: `Reminder sent successfully!`, severity: "success" });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: `Failed to send reminder.`, severity: "error" });
    }
  };

  const toggleColumn = (key) => {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // The visible columns in their defined order
  const displayColumns = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));

  const filtered = dateFilteredRows.filter((row) => {
    const matchesSearch = ALL_COLUMNS.some((col) =>
      String(row[col.key] ?? "").toLowerCase().includes(search.toLowerCase())
    );
    const matchesPending = showPending ? true : !!row["bank_name"];
    return matchesSearch && matchesPending;
  });

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const av = String(a[sortKey] ?? "").toLowerCase();
        const bv = String(b[sortKey] ?? "").toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      })
    : filtered;

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Security Deposit Refund");
    const exportCols = displayColumns.filter(c => c.key !== "actions");
    worksheet.columns = exportCols.map((col) => ({
      header: col.label,
      key: col.key,
      width: 25,
    }));
    worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    worksheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF5E6B47" } };
    sorted.forEach((row) => {
      const rowData = {};
      exportCols.forEach((col) => {
        let val = row[col.key] ?? "";
        if (col.key === "actual_checkin" || col.key === "actual_checkout" || col.key === "booking_date") {
          val = val ? new Date(val).toLocaleDateString("en-GB", { timeZone: "Asia/Karachi", day: "2-digit", month: "short", year: "numeric" }) : "";
        } else if (col.key === "bank_name") {
          val = val || "Pending";
        } else if (col.key === "account_holder_name" || col.key === "account_number") {
          val = val || "-";
        }
        rowData[col.key] = val;
      });
      worksheet.addRow(rowData);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Security_Deposit_Refund_${startDate}_to_${endDate}.xlsx`);
  };

  const hasInfo = (row) => row["account_holder_name"] || row["account_number"] || row["bank_name"];
  const totalWithInfo = dateFilteredRows.filter(hasInfo).length;
  const totalPending = dateFilteredRows.filter((r) => !hasInfo(r)).length;

  const displayLabel = startDate === endDate && startDate === getTodayPKT()
    ? new Date().toLocaleDateString("en-GB", { timeZone: "Asia/Karachi", weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    : `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;

  const renderCell = (col, row) => {
    switch (col.key) {
      case "reservation_id":
        return (
          <span style={{
            background: "#eef2e6", color: "#5e6b47", fontWeight: 700,
            fontSize: "12px", borderRadius: "6px", padding: "3px 8px",
            letterSpacing: "0.3px", fontFamily: "monospace",
          }}>
            {row["reservation_id"] ?? "-"}
          </span>
        );
      case "guest_name":
        return <span style={{ fontWeight: 600, fontSize: "13px", color: "#212121" }}>{row["guest_name"] || "-"}</span>;
      case "actual_checkin":
        return (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Icon sx={{ fontSize: "15px !important", color: "#4CAF50" }}>login</Icon>
            <span style={{ fontSize: "13px", color: "#424242" }}>{formatDateDisplay(row["actual_checkin"])}</span>
          </MDBox>
        );
      case "actual_checkout":
        return (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Icon sx={{ fontSize: "15px !important", color: "#F44336" }}>logout</Icon>
            <span style={{ fontSize: "13px", color: "#424242" }}>{formatDateDisplay(row["actual_checkout"])}</span>
          </MDBox>
        );
      case "account_holder_name":
        return <span style={{ fontSize: "13px", color: row["account_holder_name"] ? "#212121" : "#BDBDBD" }}>{row["account_holder_name"] || "-"}</span>;
      case "account_number":
        return (
          <span style={{
            fontSize: "13px",
            color: row["account_number"] ? "#5e6b47" : "#BDBDBD",
            fontFamily: row["account_number"] ? "monospace" : "inherit",
            fontWeight: row["account_number"] ? 600 : 400,
          }}>{row["account_number"] || "-"}</span>
        );
      case "bank_name":
        return row["bank_name"] ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            background: "#f0fdfa", color: "#115e59", borderRadius: "20px",
            padding: "3px 10px", fontSize: "12px", fontWeight: 600,
          }}>{row["bank_name"]}</span>
        ) : (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            background: "#fffaf0", color: "#c05621", borderRadius: "20px",
            padding: "3px 10px", fontSize: "12px", fontWeight: 600,
          }}>Pending</span>
        );
      case "booking_date":
        return <span style={{ fontSize: "13px", color: "#424242" }}>{formatDateDisplay(row["booking_date"])}</span>;
      case "total_price":
      case "total_paid":
        return <span style={{ fontSize: "13px", color: "#212121", fontWeight: 600 }}>{row[col.key] != null ? `${row["currency"] || ""}${row[col.key]}` : "-"}</span>;
      case "payment_status":
        return row[col.key] ? (
          <span style={{
            background: "#e8f5e9", color: "#2e7d32", borderRadius: "20px",
            padding: "3px 10px", fontSize: "12px", fontWeight: 600,
          }}>{row[col.key]}</span>
        ) : <span style={{ color: "#BDBDBD" }}>-</span>;
      case "cleanness_status":
        return row[col.key] ? (
          <span style={{
            background: "#e3f2fd", color: "#1565c0", borderRadius: "20px",
            padding: "3px 10px", fontSize: "12px", fontWeight: 600,
          }}>{row[col.key]}</span>
        ) : <span style={{ color: "#BDBDBD" }}>-</span>;
      case "actions":
        return !row["bank_name"] ? (
          <MDBox
            onClick={() => handleSendReminder(row["reservation_id"])}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              background: "#fff", color: "#5e6b47", border: "1px solid #5e6b47",
              borderRadius: "6px", padding: "4px 10px", fontSize: "12px",
              fontWeight: 600, cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap",
              "&:hover": { background: "#5e6b47", color: "#fff" },
            }}
          >
            <Icon sx={{ fontSize: "14px !important" }}>send</Icon>
            Reminder
          </MDBox>
        ) : (
          <span style={{ color: "#9E9E9E", fontSize: "12px" }}>—</span>
        );
      default:
        return <span style={{ fontSize: "13px", color: "#424242" }}>{row[col.key] ?? "-"}</span>;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={{ xs: 1, md: 3 }}>
        {/* Page Header */}
        <MDBox
          mb={3}
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          <MDBox>
            <MDTypography
              variant="h4"
              fontWeight="bold"
              sx={{
                background: "linear-gradient(135deg, #4a5538 0%, #5e6b47 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.5px",
              }}
            >
              Security Deposit Refund
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
              <Icon sx={{ fontSize: "16px !important", color: "#5e6b47" }}>today</Icon>
              <MDTypography variant="body2" sx={{ color: "#5e6b47", fontWeight: 600 }}>
                {displayLabel}
              </MDTypography>
              {!loading && !error && (
                <MDTypography variant="caption" sx={{ color: "#9E9E9E", ml: 1 }}>
                  &bull; {sorted.length} record{sorted.length !== 1 ? "s" : ""} in range
                </MDTypography>
              )}
            </MDBox>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {/* Date Range Filters */}
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox sx={{ display: "flex", alignItems: "center", backgroundColor: "#fff", border: "1.5px solid #E0E0E0", borderRadius: "10px", px: 1.5, py: 0.8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <MDTypography variant="caption" sx={{ color: "#666", mr: 1, fontWeight: "bold" }}>From:</MDTypography>
                <input type="date" value={startDate} onChange={handleStartDateChange} style={{ border: "none", outline: "none", fontSize: "0.85rem", fontFamily: "inherit", color: "#333", backgroundColor: "transparent" }} />
              </MDBox>
              <MDBox sx={{ display: "flex", alignItems: "center", backgroundColor: "#fff", border: "1.5px solid #E0E0E0", borderRadius: "10px", px: 1.5, py: 0.8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <MDTypography variant="caption" sx={{ color: "#666", mr: 1, fontWeight: "bold" }}>To:</MDTypography>
                <input type="date" value={endDate} onChange={handleEndDateChange} style={{ border: "none", outline: "none", fontSize: "0.85rem", fontFamily: "inherit", color: "#333", backgroundColor: "transparent" }} />
              </MDBox>
            </MDBox>

            {/* Show Pending Checkbox */}
            <FormControlLabel
              control={<Checkbox checked={showPending} onChange={(e) => setShowPending(e.target.checked)} sx={{ color: "#5e6b47", "&.Mui-checked": { color: "#5e6b47" } }} />}
              label={<MDTypography variant="button" fontWeight="medium" color="text">Show Pending</MDTypography>}
              sx={{ mr: 1 }}
            />

            {/* Column Visibility Toggle */}
            <MDBox ref={colMenuRef} sx={{ position: "relative" }}>
              <MDBox
                onClick={() => setColMenuOpen(o => !o)}
                title="Toggle Columns"
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 38, height: 38,
                  backgroundColor: colMenuOpen ? "#eef2e6" : "#fff",
                  border: `1.5px solid ${colMenuOpen ? "#5e6b47" : "#E0E0E0"}`,
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  cursor: "pointer", transition: "all 0.2s",
                  "&:hover": { borderColor: "#5e6b47", background: "#eef2e6" },
                }}
              >
                <Icon sx={{ color: "#5e6b47", fontSize: "18px !important" }}>view_column</Icon>
              </MDBox>

              {/* Dropdown */}
              {colMenuOpen && (
                <MDBox
                  sx={{
                    position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 1300,
                    background: "#fff", borderRadius: "14px",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    border: "1.5px solid #E8E8E8",
                    minWidth: "220px", maxHeight: "400px", overflowY: "auto",
                    p: 1.5,
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: "#9E9E9E", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", px: 1, pb: 0.5, display: "block" }}>
                    Show / Hide Columns
                  </MDTypography>
                  {ALL_COLUMNS.filter(c => c.key !== "actions").map((col) => (
                    <MDBox
                      key={col.key}
                      onClick={() => toggleColumn(col.key)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 1, py: 0.5, borderRadius: "8px", cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": { background: "#f5f5f5" },
                      }}
                    >
                      <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        size="small"
                        sx={{ p: 0, color: "#5e6b47", "&.Mui-checked": { color: "#5e6b47" } }}
                        onChange={() => {}}
                      />
                      <MDTypography variant="caption" sx={{ fontSize: "13px", color: "#333", fontWeight: visibleColumns.includes(col.key) ? 600 : 400 }}>
                        {col.label}
                      </MDTypography>
                    </MDBox>
                  ))}
                  {/* Reset to defaults */}
                  <MDBox
                    onClick={() => setVisibleColumns(DEFAULT_VISIBLE)}
                    sx={{
                      mt: 1, pt: 1, borderTop: "1px solid #F0F0F0",
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.5, borderRadius: "8px", cursor: "pointer",
                      color: "#c05621", "&:hover": { background: "#fffaf0" },
                    }}
                  >
                    <Icon sx={{ fontSize: "14px !important" }}>restart_alt</Icon>
                    <MDTypography variant="caption" sx={{ fontSize: "12px", color: "#c05621", fontWeight: 600 }}>Reset to Default</MDTypography>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>

            {/* Export button */}
            <MDBox onClick={exportToExcel} title="Export to Excel" sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: "10px", border: "1.5px solid #E0E0E0", background: "#fff", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", transition: "all 0.2s", mr: 1, "&:hover": { borderColor: "#5e6b47", background: "#eef2e6" } }}>
              <Icon sx={{ color: "#5e6b47", fontSize: "18px !important" }}>download</Icon>
            </MDBox>

            {/* Refresh button */}
            <MDBox onClick={fetchAllRecords} title="Refresh" sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: "10px", border: "1.5px solid #E0E0E0", background: "#fff", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", transition: "all 0.2s", "&:hover": { borderColor: "#5e6b47", background: "#eef2e6" } }}>
              <Icon sx={{ color: "#5e6b47", fontSize: "18px !important" }}>refresh</Icon>
            </MDBox>

            {/* Search */}
            <MDBox display="flex" alignItems="center" sx={{ background: "#fff", borderRadius: "12px", border: "1.5px solid #E0E0E0", px: 2, py: 0.8, minWidth: { xs: "100%", sm: "240px" }, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", transition: "border-color 0.2s", "&:focus-within": { borderColor: "#5e6b47" } }}>
              <Icon sx={{ color: "#9E9E9E", mr: 1, fontSize: "20px !important" }}>search</Icon>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search records..." style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontSize: "14px", color: "#212121", fontFamily: "inherit" }} />
              {search && (
                <Icon onClick={() => setSearch("")} sx={{ color: "#9E9E9E", ml: 1, fontSize: "18px !important", cursor: "pointer", "&:hover": { color: "#5e6b47" } }}>close</Icon>
              )}
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Stats Cards */}
        {!loading && !error && (
          <MDBox display="flex" gap={2} mb={3} flexWrap="wrap">
            {[
              { label: "Checkouts in Range", value: dateFilteredRows.length, icon: "logout", color: "#5e6b47", bg: "linear-gradient(135deg, #eef2e6, #d6e0c3)" },
              { label: "Bank Info Provided", value: totalWithInfo, icon: "account_balance", color: "#115e59", bg: "linear-gradient(135deg, #f0fdfa, #ccfbf1)" },
              { label: "Pending Bank Info", value: totalPending, icon: "pending_actions", color: "#c05621", bg: "linear-gradient(135deg, #fffaf0, #feebc8)" },
            ].map((stat) => (
              <MDBox key={stat.label} sx={{ background: "#ffffff", borderRadius: "16px", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2.5, flex: "1 1 220px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.04)", transition: "all 0.2s ease-in-out", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 30px rgba(0,0,0,0.08)" } }}>
                <MDBox sx={{ background: stat.bg, borderRadius: "12px", width: "54px", height: "54px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 15px ${stat.color}20` }}>
                  <Icon sx={{ color: stat.color, fontSize: "26px !important" }}>{stat.icon}</Icon>
                </MDBox>
                <MDBox display="flex" flexDirection="column">
                  <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#2d3748", lineHeight: 1.1, mb: 0.5 }}>{stat.value}</MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#718096", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}>{stat.label}</MDTypography>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        )}

        {/* Table Card */}
        <MDBox sx={{ background: "#fff", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          {loading ? (
            <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
              <CircularProgress sx={{ color: "#5e6b47" }} />
              <MDTypography variant="body2" sx={{ color: "#9E9E9E" }}>Loading records...</MDTypography>
            </MDBox>
          ) : error ? (
            <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
              <Icon sx={{ fontSize: "48px !important", color: "#EF5350" }}>error_outline</Icon>
              <MDTypography variant="body2" sx={{ color: "#EF5350" }}>{error}</MDTypography>
              <MDBox onClick={fetchAllRecords} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", color: "#5e6b47", fontWeight: 600, fontSize: "14px", border: "1.5px solid #5e6b47", borderRadius: "8px", px: 2, py: 0.8, "&:hover": { background: "#eef2e6" } }}>
                <Icon sx={{ fontSize: "16px !important" }}>refresh</Icon>
                Try Again
              </MDBox>
            </MDBox>
          ) : (
            <>
              <MDBox sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "750px" }}>
                  <thead>
                    <tr style={{ background: "linear-gradient(135deg, #4a5538 0%, #5e6b47 100%)" }}>
                      {displayColumns.map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          style={{ padding: "14px 16px", textAlign: "left", color: "#fff", fontWeight: 600, fontSize: "12px", letterSpacing: "0.5px", textTransform: "uppercase", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            {col.label}
                            <span style={{ opacity: sortKey === col.key ? 1 : 0.4, fontSize: "16px" }}>
                              {sortKey === col.key ? (sortDir === "asc" ? "\u2191" : "\u2193") : "\u2195"}
                            </span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr>
                        <td colSpan={displayColumns.length} style={{ textAlign: "center", padding: "56px 16px", color: "#9E9E9E", fontSize: "14px" }}>
                          <div style={{ marginBottom: "8px", fontSize: "40px" }}>🏖️</div>
                          {search ? "No records match your search." : "No checkouts scheduled for today."}
                        </td>
                      </tr>
                    ) : (
                      sorted.map((row, idx) => (
                        <tr
                          key={row["reservation_id"] ?? idx}
                          style={{ background: idx % 2 === 0 ? "#FAFAFA" : "#fff", borderBottom: "1px solid #F0F0F0", transition: "background 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#EEF2FF"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? "#FAFAFA" : "#fff"; }}
                        >
                          {displayColumns.map((col) => (
                            <td key={col.key} style={{ padding: "13px 16px" }}>
                              {renderCell(col, row)}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </MDBox>

              <MDBox px={2} py={1.5} sx={{ borderTop: "1px solid #F0F0F0", background: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <MDTypography variant="caption" sx={{ color: "#9E9E9E" }}>
                  Showing {sorted.length} of {dateFilteredRows.length} checkout{dateFilteredRows.length !== 1 ? "s" : ""} in range
                </MDTypography>
              </MDBox>
            </>
          )}
        </MDBox>
      </MDBox>
      <Footer />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default SecurityDepositRefund;
